import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { OperateAbstract } from './OperateAbstract';
import { EEntityType, IRotatedBox } from '@/editor/types';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { MultiModelAbsorbManager } from '@/editor/manager/absorb/box/multiModelAbsorbManager';
import { VirtualMultiModelEntities } from '@/editor/entities/virtualModelEntities';
import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import { adsorbModelInfos } from '@/editor/data/multiModel/utils';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import BaseEntity from '@/editor/entities/BaseEntity';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';

export class OperateMultiModel extends OperateAbstract {
  checkedEntity: BaseEntity<EEntityType.Base> | null | undefined;
  private _virtualEntities = new VirtualMultiModelEntities();

  private _virtualRulerModel = new RulerModel();
  private _extraOulines: { start: IPoint2d; end: IPoint2d }[] = [];

  private _multiModelAbsorbManager = new MultiModelAbsorbManager();
  private _checkedOffset: IPoint2d = { x: 0, y: 0 };

  private _originalMultiModelData: IMultiModelData | null = null;
  private _movedMultiModelData: IMultiModelData | null = null;

  private _selectedBoxes = new OperateSelectedBoxes();

  constructor() {
    super();
  }

  get selected() {
    return globalStores.editorStateStore.selected;
  }

  private updateVirtualEntity() {
    if (!this._movedMultiModelData) return;

    this._virtualEntities.update(this._movedMultiModelData);
  }

  private getMultiModelAdsorbBoxData(center: IPoint2d, originBoxData: IRotatedBox) {
    const boxData = { ...originBoxData };
    boxData.center = center;

    const absorbResult = this._multiModelAbsorbManager.calculateThenRender(boxData);
    if (absorbResult) {
      boxData.center = absorbResult.absorbPoint;
      boxData.rotation = absorbResult.rotation;
    }

    return boxData;
  }

  override startMove(point: IPoint2d) {
    const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
    if (!multiModelData) return;

    this._originalMultiModelData = multiModelData;

    this._virtualEntities.create(multiModelData);

    this._multiModelAbsorbManager.updateData({
      excludeModelIds: [...this._virtualEntities.furnitureIds, ...this._virtualEntities.assemblyIds]
    });

    this._virtualEntities.setRealEntitiesVisible(false);
    this.updateVirtualEntity();

    this._extraOulines = getBoxRayDetectOutlines(this._virtualEntities.furnitureIds);
    const rulerInfos = calculateBoxRulerInfos(multiModelData, this._extraOulines);

    this._virtualRulerModel.updateModel(rulerInfos);

    // 多模型中心点与拾取点的偏移
    this._checkedOffset = Point2dUtils.subtracted(point, multiModelData.center);
    this.updateBoxes();
  }

  move(point: IPoint2d) {
    if (!this._originalMultiModelData) return point;

    this.moving = true;

    const adsorbBoxData = this.getMultiModelAdsorbBoxData(
      Point2dUtils.subtracted(point, this._checkedOffset),
      this._originalMultiModelData
    );

    this.updateMovedMultiModel(adsorbBoxData);
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this._originalMultiModelData) return;

    this.moving = true;

    // 累积在移动后的数据上，但映射的源始终是原始数据，避免累积误差
    const base = this._movedMultiModelData ?? this._originalMultiModelData;
    this.updateMovedMultiModel({
      ...this._originalMultiModelData,
      center: Point2dUtils.added(base.center, offset),
      rotation: base.rotation
    });
  }

  /** 按目标包围盒更新虚拟实体、标尺和选中框 */
  private updateMovedMultiModel(boxData: IRotatedBox) {
    if (!this._originalMultiModelData) return;

    const moveModelInfos = adsorbModelInfos(
      this._originalMultiModelData.modelInfos,
      boxData,
      this._originalMultiModelData
    );
    this._movedMultiModelData = {
      ...this._originalMultiModelData,
      modelInfos: moveModelInfos,
      center: boxData.center,
      rotation: boxData.rotation
    };

    this.updateVirtualEntity();

    const rulerInfos = calculateBoxRulerInfos(boxData, this._extraOulines);
    this._virtualRulerModel.updateModel(rulerInfos);

    this.updateBoxes({ x: boxData.center.x, y: boxData.center.y, rotation: boxData.rotation });
  }

  completeMove(_point: IPoint2d) {
    if (!this._movedMultiModelData || !this.moving) return;

    this.moving = false;

    globalStores.multiModelOperation.modifyMultiModel(this._movedMultiModelData.modelInfos);

    globalStores.editorStateStore.setSelected(
      {
        entityId: MULTI_SELECT_TEMPORARY_ID,
        type: EEntityType.MultiModel
      },
      { multiModelData: this._movedMultiModelData }
    );

    this.clear();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._originalMultiModelData) return;

    this._selectedBoxes.update({ type: EEntityType.MultiModel, entityId: MULTI_SELECT_TEMPORARY_ID }, params);
  }

  clear() {
    this._multiModelAbsorbManager.clearModel();
    this._virtualRulerModel.clear();
    this._virtualEntities.setRealEntitiesVisible(true);
    this._virtualEntities.dispose();
    this._selectedBoxes.clear();

    this.checkedEntity = null;
    this.moving = false;
    this.pickPoint = null;
    this._checkedOffset = { x: 0, y: 0 };
    this._originalMultiModelData = null;
    this._movedMultiModelData = null;
  }

  dispose() {
    this.clear();
    this._multiModelAbsorbManager.dispose();
    this._virtualRulerModel.dispose();
    this._selectedBoxes.dispose();
  }
}
